import { useState } from "react";
import { motion } from "framer-motion";
import { MessageCircle, MapPin, Phone, Mail, Receipt, Clock, Warehouse } from "lucide-react";
import { Seo } from "@/components/site/Seo";
import { LineReveal, FadeUp } from "@/components/site/Reveal";
import { SITE, CATEGORIES, waLink } from "@/data/site";

const DETAILS = [
  { icon: MapPin, label: "Store address", value: SITE.addressLines.join(", "), href: SITE.mapLink, id: "address" },
  { icon: Phone, label: "Call or WhatsApp", value: SITE.phone, href: `tel:${SITE.phoneRaw}`, id: "phone" },
  { icon: Mail, label: "Email", value: SITE.email, href: `mailto:${SITE.email}`, id: "email" },
  { icon: Receipt, label: "GSTIN", value: SITE.gstin, id: "gstin" },
  { icon: Clock, label: "Store hours", value: "10 AM – 9 PM", id: "hours" },
  { icon: Warehouse, label: "Godown hours", value: "9 AM – 6 PM", id: "godown-hours" },
];

const WaForm = () => {
  const [f, setF] = useState({ name: "", phone: "", category: CATEGORIES[0].title, message: "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = (e) => {
    e.preventDefault();
    const text = `Hi Rama Electricals,\n\nName: ${f.name}\nPhone: ${f.phone}\nInterested in: ${f.category}\n\n${f.message}`;
    window.open(waLink(text), "_blank", "noopener");
  };
  return (
    <form onSubmit={submit} className="glass rounded-[28px] p-6 md:p-10 space-y-5" data-testid="whatsapp-form">
      <div className="grid sm:grid-cols-2 gap-5">
        <label className="block text-sm text-slate-300">Your name<input required value={f.name} onChange={set("name")} className="field mt-2" placeholder="Full name" data-testid="form-name-input" /></label>
        <label className="block text-sm text-slate-300">Phone<input required value={f.phone} onChange={set("phone")} className="field mt-2" placeholder="10-digit mobile" inputMode="tel" data-testid="form-phone-input" /></label>
      </div>
      <label className="block text-sm text-slate-300">I'm looking for
        <select value={f.category} onChange={set("category")} className="field mt-2" data-testid="form-category-select">
          {CATEGORIES.map((c) => <option key={c.id}>{c.title}</option>)}
          <option>Something else</option>
        </select>
      </label>
      <label className="block text-sm text-slate-300">Message<textarea required rows={5} value={f.message} onChange={set("message")} className="field mt-2 resize-none" placeholder="Brands, quantities or your full material list" data-testid="form-message-input" /></label>
      <button type="submit" className="btn-neon w-full justify-center" data-testid="whatsapp-submit-btn"><MessageCircle size={18} /> Send on WhatsApp</button>
      <p className="text-xs text-[#a5b4c8] text-center">Opens WhatsApp with your message ready to send to {SITE.phone}.</p>
    </form>
  );
};

export default function Contact() {
  return (
    <>
      <Seo path="/contact" title="Contact Rama Electricals | Goregaon East, Mumbai 400065"
        description="Contact Rama Electricals at A321, MasterMind IV, Royal Palms, Goregaon East, Mumbai 400065. Call or WhatsApp +91 93221 26690 or email hello@rama-electricals.com for quotes." />
      <section className="px-6 pt-40 pb-16" data-testid="contact-hero">
        <div className="mx-auto max-w-7xl">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="eyebrow mb-8">Contact us</motion.p>
          <h1 className="font-display font-extrabold tracking-tight leading-[0.95] text-[44px] sm:text-6xl lg:text-[96px]">
            <LineReveal onLoad delay={0.15} lines={["Let's light up", <span key="c" className="neon-text">your next project.</span>]} />
          </h1>
        </div>
      </section>
      <section className="px-6 pb-20">
        <div className="mx-auto max-w-7xl grid lg:grid-cols-12 gap-8">
          <FadeUp className="lg:col-span-7"><WaForm /></FadeUp>
          <div className="lg:col-span-5 space-y-3">
            {DETAILS.map(({ icon: Icon, label, value, href, id }, i) => {
              const Tag = href ? "a" : "div";
              return (
                <FadeUp key={id} delay={i * 0.06}>
                  <Tag {...(href ? { href, target: href.startsWith("http") ? "_blank" : undefined, rel: "noreferrer" } : {})} data-testid={`contact-${id}`}
                    className="glass rounded-2xl p-5 flex gap-4 items-start transition-colors duration-300 hover:border-[#39FF14]/40">
                    <span className="h-11 w-11 shrink-0 rounded-xl grid place-items-center bg-[#39FF14]/10 text-[#39FF14]"><Icon size={20} /></span>
                    <span><span className="block font-mono text-[11px] tracking-[0.2em] uppercase text-[#a5b4c8]">{label}</span><span className="block mt-1 text-white break-words">{value}</span></span>
                  </Tag>
                </FadeUp>
              );
            })}
          </div>
        </div>
      </section>
      <section className="px-6 pb-28" data-testid="map-section">
        <FadeUp className="mx-auto max-w-7xl rounded-[28px] overflow-hidden border border-[#39FF14]/20 aspect-[4/5] sm:aspect-[16/7]">
          <iframe title="Rama Electricals location map, Goregaon East" src={SITE.mapSrc} className="h-full w-full" loading="lazy" referrerPolicy="no-referrer-when-downgrade"
            style={{ filter: "invert(90%) hue-rotate(180deg) saturate(0.8) contrast(0.9)" }} data-testid="google-map" />
        </FadeUp>
      </section>
    </>
  );
}
